import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'

import ActionButton from '../ActionButton/ActionButton'

import getImageNaturalDimensions from '../../../services/dom/getImageNaturalDimensions'

const Base = styled.div`
  position: relative;
  margin-top: 1rem;
  margin-bottom: 120px;
`

const ImageBase = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
`

const ImageWrapper = styled.div`
  overflow: hidden;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background-color: #eeeeee;
  text-align: left;
  border-radius: ${props => (props.variant === 'circle' ? '50%' : 0)};
`

const StyledImage = styled.img.attrs(({ top, left }) => ({
  style: {
    top: `${top}px`,
    left: `${left}px`,
  },
}))`
  position: absolute;
  cursor: move;
`

const GhostStyledImage = styled(StyledImage)`
  opacity: 0.2;
  cursor: default;
`

const ControlWrapper = styled.div`
  position: absolute;
  text-align: center;
  width: 100%;
`

const ImageZoomSlider = styled.input`
  display: block;
  width: 100%;
  margin: 1rem 0;
`

const CropButton = styled(ActionButton)`
  &:hover {
    background: var(--color-primary);
    color: #ffffff;
  }
`

class ImageCropUpload extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      valuePositionX: 0,
      valuePositionY: 0,
      imageNaturalHeight: 0,
      imageNaturalWidth: 0,
      scaleValue: 100,
    }

    this.container = React.createRef()
    this.cropper = React.createRef()
    this.image = React.createRef()

    this.handleMouseDown = this.handleMouseDown.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleMouseUp = this.handleMouseUp.bind(this)
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.handleRender = this.handleRender.bind(this)
    this.handleCrop = this.handleCrop.bind(this)
  }

  async getNaturalImageSize(cb) {
    const { current: image } = this.image

    const { height, width } = getImageNaturalDimensions(image)

    this.setState(
      {
        imageNaturalHeight: height,
        imageNaturalWidth: width,
      },
      cb,
    )
  }

  async getInitialScales() {
    this.getNaturalImageSize(() => {
      const { imageNaturalWidth, imageNaturalHeight } = this.state
      const { current: cropper } = this.cropper
      const { clientWidth, clientHeight } = cropper

      const ratio =
        clientWidth > clientHeight
          ? clientWidth / imageNaturalWidth
          : clientHeight / imageNaturalHeight

      const scaleValue = parseFloat(ratio * 100, 10).toFixed(1)

      this.setState({
        scaleValue,
        valuePositionY: 0,
        valuePositionX: 0,
        cropperHeight: clientHeight,
        cropperWidth: clientWidth,
      })
    })
  }

  handleZoomChange = e => {
    const { value: scaleValue } = e.target

    this.setState({ scaleValue: parseFloat(scaleValue, 10).toFixed(1) })
  }

  handleMouseUp() {
    if (!this.state) {
      return
    }

    window.removeEventListener('mouseup', this.handleMouseUp)
    window.removeEventListener('mousemove', this.handleMouseMove)

    const { dragged } = this.state

    if (!dragged) {
      return
    }

    this.setState({
      dragged: false,
      clickX: null,
      clickY: null,
    })
  }

  handleTouchStart(e) {
    if (!this.state) {
      return
    }

    const { valuePositionX, valuePositionY } = this.state

    const touch = e.touches[0]

    this.setState({
      clickX: touch.clientX,
      clickY: touch.clientY,
      lastValuePositionX: valuePositionX,
      lastValuePositionY: valuePositionY,
      dragged: true,
    })
  }

  handleTouchMove(e) {
    if (!this.state) {
      return
    }

    const {
      dragged,
      clickX,
      clickY,
      lastValuePositionX,
      lastValuePositionY,
    } = this.state

    const touch = e.changedTouches[0]

    if (dragged) {
      const translateX = touch.clientX - clickX
      const translateY = touch.clientY - clickY

      this.setState({
        data: touch.clientX,
        valuePositionX: lastValuePositionX + translateX,
        valuePositionY: lastValuePositionY + translateY,
      })
    }
  }

  handleTouchEnd() {
    if (!this.state) {
      return
    }

    const { dragged } = this.state

    if (!dragged) {
      return
    }

    this.setState({
      dragged: false,
      clickX: null,
      clickY: null,
    })
  }

  handleMouseMove(e) {
    if (!this.state) {
      return
    }

    const {
      dragged,
      clickX,
      clickY,
      lastValuePositionX,
      lastValuePositionY,
    } = this.state

    if (dragged) {
      const translateX = e.clientX - clickX
      const translateY = e.clientY - clickY

      this.setState({
        valuePositionX: lastValuePositionX + translateX,
        valuePositionY: lastValuePositionY + translateY,
      })
    }
  }

  handleMouseDown(e) {
    e.preventDefault()
    e.stopPropagation()

    const { valuePositionX, valuePositionY } = this.state

    window.addEventListener('mouseup', this.handleMouseUp)
    window.addEventListener('mousemove', this.handleMouseMove)

    this.setState({
      clickX: e.clientX,
      clickY: e.clientY,
      lastValuePositionX: valuePositionX,
      lastValuePositionY: valuePositionY,
      dragged: true,
    })
  }

  handleCrop() {
    const { src, onCrop } = this.props

    const {
      cropperWidth,
      cropperHeight,
      valuePositionX,
      valuePositionY,
      scaleValue,
      imageNaturalWidth,
      imageNaturalHeight,
    } = this.state

    const image = new Image()

    const canvas = document.createElement('canvas')
    canvas.width = cropperWidth
    canvas.height = cropperHeight
    const ctx = canvas.getContext('2d')

    image.src = src

    ctx.drawImage(
      image,
      valuePositionX,
      valuePositionY,
      (imageNaturalWidth * scaleValue) / 100,
      (imageNaturalHeight * scaleValue) / 100,
    )

    return new Promise((resolve, reject) => {
      try {
        resolve(onCrop(canvas.toDataURL('image/png')))
      } catch (e) {}
    })
  }

  handleRender() {
    this.getInitialScales()
  }

  render() {
    const { variant, src, ...theProps } = this.props
    const {
      valuePositionX,
      valuePositionY,
      scaleValue,
      imageNaturalHeight,
      imageNaturalWidth,
    } = this.state

    return (
      <Base ref={this.container}>
        <ImageBase {...theProps}>
          <ImageWrapper ref={this.cropper} variant={variant}>
            <StyledImage
              src={src}
              ref={this.image}
              top={valuePositionY}
              left={valuePositionX}
              onLoad={this.handleRender}
              onMouseDown={this.handleMouseDown}
              onTouchStart={this.handleTouchStart}
              onTouchMove={this.handleTouchMove}
              onTouchEnd={this.handleTouchEnd}
              height={(imageNaturalHeight * scaleValue) / 100}
              width={(imageNaturalWidth * scaleValue) / 100}
            />
          </ImageWrapper>
          <GhostStyledImage
            top={valuePositionY}
            left={valuePositionX}
            src={src}
            height={(imageNaturalHeight * scaleValue) / 100}
            width={(imageNaturalWidth * scaleValue) / 100}
          />
        </ImageBase>
        <ControlWrapper>
          <ImageZoomSlider
            type="range"
            step="any"
            onChange={this.handleZoomChange}
            value={scaleValue}
            min="0"
            max="100"
          />
          <CropButton onClick={this.handleCrop}>Crop</CropButton>
        </ControlWrapper>
      </Base>
    )
  }
}

ImageCropUpload.defaultProps = {
  variant: 'circle',
  src: null,
}

ImageCropUpload.propTypes = {
  variant: PropTypes.oneOf(['circle', 'square']),
  src: PropTypes.string,
}

export default ImageCropUpload
